// Полноэкранный предпросмотр урока с прохождением (п.17 бэк-лога).
// Смонтирован ТОЛЬКО пока открыт: состояние прохождения обязано умирать
// вместе с диалогом, а постоянно смонтированный он перерисовывался бы на
// каждое нажатие клавиши в блоке (читает полный контекст черновика).
import { useEffect, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { Dialog, DialogContent, DialogDescription } from '@teach/ui'
import type { CourseWithoutBlocks } from '@/types/course'
import { useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setDialogOpen } from '@/features/editor/editorSlice'
import { hasTranslationRow } from '@/features/editor/lib/courseTranslation'
import { useBlockDraftContext } from '../studio/BlockDraftContext'
import { useHasStartedTranslationRun } from '../studio/useBlocksInRun'
import { LessonRunView } from './LessonRunView'
import { PreviewHeaderControls } from './PreviewHeaderControls'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
}

export const LessonPreviewDialog = ({ course }: Props) => {
  const m = useMessages(messages)
  const dispatch = useAppDispatch()
  const open = useAppSelector((s) => s.editor.dialogs.previewOpen)
  const storeLessonId = useAppSelector((s) => s.editor.selectedLessonId)
  const storeLang = useAppSelector((s) => s.editor.contentLang)

  // Урок и язык ПОКАЗА — локальные (п.5): селектор в шапке не должен двигать
  // ни выбранный урок редактора (`selectLesson` заодно чистит мультивыбор
  // блоков), ни `contentLang` — язык правки. Смотреть ≠ править.
  // Стор при этом остаётся источником: пока автор не тронул селекторы, значение
  // следует за редактором, поэтому синхронизация эффектом, а не засевом на маунте.
  const [lessonId, setLessonId] = useState(storeLessonId)
  const [lang, setLang] = useState(storeLang)
  useEffect(() => setLessonId(storeLessonId), [storeLessonId])
  useEffect(() => setLang(storeLang), [storeLang])

  const { data: blocks = [] } = useGetLessonBlocksQuery(lessonId ?? skipToken)
  const { draft } = useBlockDraftContext()
  const hasStartedRun = useHasStartedTranslationRun(course.id)

  // Несохранённая правка открытого блока обязана быть видна: иначе автор жмёт
  // «Предпросмотр» сразу после правки и репетирует старый экран.
  const shown = blocks.map((b) => (draft && draft.id === b.id ? draft : b))
  const close = () => dispatch(setDialogOpen({ dialog: 'previewOpen', open: false }))

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      {/* `!max-h-none overflow-visible` снимают предел высоты базового окна
          (`@teach/ui`): предпросмотр — во весь экран, прокрутка у него своя.
          `!` обязателен: tailwind-merge 2.x не узнаёт `max-h-[calc(100dvh-…)]`
          и оставляет оба класса, а исход тогда решал бы порядок в CSS. */}
      <DialogContent
        className="h-dvh !max-h-none w-screen max-w-none gap-0 overflow-visible rounded-none border-0 p-0 sm:rounded-none"
        hideCloseButton
      >
        <DialogDescription className="sr-only">{m.description}</DialogDescription>
        {/* React-`key` тут больше нет: он уносил вместе с прохождением и шапку,
            а в ней живёт селектор урока — контрол размонтировался ровно в момент
            выбора, и фокус улетал на контейнер диалога. Сброс на смене урока
            объявляет сам `useLessonRun` по `lessonId`. */}
        <LessonRunView
          blocks={shown}
          lessonId={lessonId}
          contentLang={lang}
          onClose={close}
          controls={
            <PreviewHeaderControls
              lessons={course.lessons}
              lessonId={lessonId}
              onLessonChange={setLessonId}
              lang={lang}
              onLangChange={setLang}
              hasSecondLang={hasTranslationRow(course, hasStartedRun)}
            />
          }
        />
      </DialogContent>
    </Dialog>
  )
}
